<ng-template #utilityBarForm>
  <placement-utility-header [placementData]="placementData" [placementMaintenance]="placementMaintenance"></placement-utility-header>
</ng-template>

<div class="tw:p-3 tw:pb-[60px] tw:sm:p-5 tw:sm:px-7 tw:sm:pb-10 tw:bg-[#ffffff] tw:min-h-full tw:box-border">

  @if (report) {

  <!-- Back link -->
  <a class="back-link tw:inline-flex tw:items-center tw:gap-1.5 tw:text-xs tw:mb-3.5 tw:cursor-pointer" (click)="backToProfile()">
    <mat-icon class="tw:!w-4 tw:!h-4 tw:!text-base tw:!leading-4">chevron_left</mat-icon>
    Back to learner profile
  </a>

  <!-- Page header -->
  <div class="tw:flex tw:items-start tw:justify-between tw:mb-[18px]">
    <h1 class="page-title">Progress report</h1>
    <div class="tw:flex tw:items-center tw:gap-2">
      <span class="tw:text-[#999] tw:text-xs">Status:</span>
      <!-- Same status -> colour/icon map as the session calendar tile and add-session-v2. -->
      <app-session-status-pill [status]="report.status"></app-session-status-pill>
      @if (canDebug) {
        <button mat-stroked-button type="button" class="tw:!text-[12px]" (click)="openDebugDialog()">Debug</button>
      }
    </div>
  </div>

  <mat-card appearance="outlined" class="tw:overflow-hidden">
    <div class="tw:p-4 tw:sm:px-5">

      <!-- Title row -->
      <div class="tw:relative tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-2 tw:pb-3.5 tw:border-b tw:border-b-[#eee]">
        <span class="tw:text-xs tw:text-[#888] tw:sm:absolute tw:sm:left-0 tw:w-full tw:sm:w-auto tw:text-center tw:order-first tw:sm:order-none">{{ report.date | date:'dd/MM/yyyy' }}</span>
        <span class="tw:text-[16px] tw:font-semibold tw:text-[#222]">6-week progress report</span>
      </div>

      <!-- Two-column meta: Academic progress | SEMH update -->
      <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6 tw:mt-4">

        <!-- Academic progress -->
        <div class="tw:flex tw:flex-col tw:gap-4">
          <div class="section-label">Academic progress</div>

          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Assessment score %</span>
            <leap-v2-scale-slider [(value)]="report.assessmentScorePct"
                                  [min]="0" [max]="100"
                                  display="percent"
                                  [labels]="percentTicks"
                                  [disabled]="isLocked">
            </leap-v2-scale-slider>
            @if (report.previous) {
              <leap-v2-delta [previous]="report.previous.assessmentScorePct"
                [current]="report.assessmentScorePct" display="percent"></leap-v2-delta>
              <span class="tw:text-[11px] tw:text-[#888]">
                {{report.previous.fromIlp ? 'Measured from ILP baseline' : 'Measured from previous report'}},
                {{report.previous.date | date: 'dd MMM yyyy'}}
                @if (report.targetScorePct !== null) { · target {{report.targetScorePct}}% }
              </span>
            }
          </div>
        </div>

        <!-- SEMH update -->
        <div class="tw:flex tw:flex-col tw:gap-4 tw:md:border-l tw:md:border-l-[#eee] tw:md:pl-6 tw:border-t tw:border-t-[#eee] tw:pt-4 tw:md:border-t-0 tw:md:pt-0">
          <div class="section-label">SEMH update</div>

          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Confidence</span>
            <leap-v2-scale-slider [(value)]="report.semh.confidence" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
            @if (report.previous) {
              <leap-v2-delta [previous]="report.previous.confidence" [current]="report.semh.confidence"></leap-v2-delta>
            }
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Resilience</span>
            <leap-v2-scale-slider [(value)]="report.semh.resilience" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
            @if (report.previous) {
              <leap-v2-delta [previous]="report.previous.resilience" [current]="report.semh.resilience"></leap-v2-delta>
            }
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Independence</span>
            <leap-v2-scale-slider [(value)]="report.semh.independence" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
            @if (report.previous) {
              <leap-v2-delta [previous]="report.previous.independence" [current]="report.semh.independence"></leap-v2-delta>
            }
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Motivation</span>
            <leap-v2-scale-slider [(value)]="report.semh.motivation" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
            @if (report.previous) {
              <leap-v2-delta [previous]="report.previous.motivation" [current]="report.semh.motivation"></leap-v2-delta>
            }
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Self-regulation</span>
            <leap-v2-scale-slider [(value)]="report.semh.selfRegulation" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
            @if (report.previous) {
              <leap-v2-delta [previous]="report.previous.selfRegulation" [current]="report.semh.selfRegulation"></leap-v2-delta>
            }
          </div>
        </div>

      </div><!-- /two-column meta -->

      <!-- Summary of last 6 weeks' progress -->
      <div class="tw:mt-5 tw:pt-4 tw:border-t tw:border-t-[#eee]">
        <label class="field__label">Summary of last 6 weeks' progress</label>
        <textarea class="field__control tw:w-full"
                  [(ngModel)]="report.progressSummary"
                  [ngModelOptions]="{standalone: true}"
                  [readonly]="isLocked"
                  rows="4"
                  [maxlength]="maxChars"
                  placeholder="Summarise the learner's overall progress across the last 6 weeks — what has been covered, how the learner has responded, and any notable developments."></textarea>
        <span class="field__hint tw:block tw:text-right">{{ report.progressSummary.length }} / {{ maxChars }}</span>
      </div>

      <!-- Plan for the next 6 weeks -->
      <div class="tw:mt-3">
        <label class="field__label">Plan for the next 6 weeks</label>
        <textarea class="field__control tw:w-full"
                  [(ngModel)]="report.nextPlan"
                  [ngModelOptions]="{standalone: true}"
                  [readonly]="isLocked"
                  rows="4"
                  [maxlength]="maxChars"
                  placeholder="Outline the learning objectives and approach for the next 6 weeks, including any adjustments to strategy or focus areas based on progress so far."></textarea>
        <span class="field__hint tw:block tw:text-right">{{ report.nextPlan.length }} / {{ maxChars }}</span>
      </div>

      <!-- Evidence of work -->
      <div class="tw:mt-3.5">
        <label class="field__label">Evidence of work</label>
        <!-- Files already stored for this report. Each opens through the API, which checks the caller may see it.
             A draft's X takes the file off the list; the next save removes it. A locked report keeps its files. -->
        @if (shownSavedEvidence.length > 0) {
          <ul class="saved-evidence tw:list-none tw:p-0 tw:mb-2 tw:flex tw:flex-col tw:gap-[5px]">
            @for (f of shownSavedEvidence; track f.url) {
              <li class="tw:text-xs tw:flex tw:items-center tw:gap-2">
                <a href="" (click)="openEvidence($event, f)">{{ f.name }}</a>
                @if (!isLocked) {
                  <button mat-icon-button type="button" class="remove-saved remove-btn tw:!w-6 tw:!h-6 tw:![line-height:24px]"
                    [attr.aria-label]="'Remove ' + f.name" (click)="removeSavedEvidence(f)">
                    <mat-icon>close</mat-icon>
                  </button>
                }
              </li>
            }
          </ul>
        }
        <leap-v2-evidence-upload (filesChanged)="onEvidenceChanged($event)" [disabled]="isLocked"></leap-v2-evidence-upload>
      </div>

    </div>
  </mat-card>

  <!-- Footer actions -->
  <mat-card appearance="outlined" class="tw:mt-4">
    <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:p-3 tw:sm:px-4">
      <div class="tw:flex tw:flex-wrap tw:gap-2">
        @if (reportFinalised) {
          <!-- Approved is terminal for sign-off: no Save/Submit and no Accept/Reject. Unlock is the way back. -->
          <button *checkFeatureRender="'UnlockProgressReports'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="unlockProgressReport()">Unlock</button>
        } @else if (reportSubmitted && canAuthorise) {
          <!-- A submitted report is signed off by whoever holds the progress-report approve/reject features:
               Accept records the QA sign-off and makes it visible to the client, Reject returns it to the tutor
               with a reason. Each button is gated on its own feature, and the tutor — who holds neither — falls
               through to the Save/Submit branch below rather than seeing an empty footer. -->
          @if (canReject) {
            <button mat-stroked-button color="warn" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="rejectProgressReport()">Reject</button>
          }
          @if (canApprove) {
            <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="acceptProgressReport()">Accept</button>
          }
          <button *checkFeatureRender="'UnlockProgressReports'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="!isLocked" (click)="unlockProgressReport()">Unlock</button>
        } @else {
          <!-- Draft or Rejected: the tutor's Save / Submit. A rejected report is corrected and resubmitted
               directly — there is no unlock step. -->
          <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="isLocked" (click)="saveProgressReport()">Save</button>
          <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="!canSubmit || isLocked" (click)="submitProgressReport()">Submit</button>
          <button *checkFeatureRender="'UnlockProgressReports'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="!isLocked" (click)="unlockProgressReport()">Unlock</button>
        }
      </div>
      <button mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="backToProfile()">Close</button>
    </div>
  </mat-card>

  } @else {
  <div class="tw:text-[13px] tw:text-[#888] tw:p-4">Loading report…</div>
  }

</div>
